import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { countries } from '@road/pkg-js';

import { startOfMonth, endOfMonth } from 'date-fns';

import { Modal, Form, Label, Icon, Grid, Checkbox, Button } from 'semantic';

const getDefaultState = () => ({
  open: false,
});

const getDefaultFilters = () => ({
  useAllTime: true,
  hideUsed: true,
  enableAutoPick: false,
  from: startOfMonth(new Date()),
  to: endOfMonth(new Date()),
  countryCode: null,
});

export default class Filters extends React.Component {
  state = {
    ...getDefaultState(),
    ...getDefaultFilters(),
    appliedFilters: [],
    countryCodes: [],
  };

  open = () => this.setState({ open: true });
  close = () => this.setState(getDefaultState());

  computeFilters() {
    const { from, to, useAllTime, hideUsed, enableAutoPick, countryCode } =
      this.state;

    const filters = [];
    if (!useAllTime) {
      filters.push({
        field: 'dateRange',
        value: {
          from,
          to,
        },
        apiFilter: {
          from,
          to,
        },
        label: `Date Range ${from.toLocaleDateString()}-${to.toLocaleDateString()}`,
      });
    }

    if (hideUsed) {
      filters.push({
        field: 'hideUsed',
        value: true,
        apiFilter: {
          hideUsed: true,
        },
        label: 'Only show unused',
      });
    }

    if (enableAutoPick) {
      filters.push({
        field: 'enableAutoPick',
        value: true,
        apiFilter: {
          enableAutoPick: true,
        },
        label: 'Enable Auto Pick',
      });
    }

    if (countryCode) {
      filters.push({
        field: 'countryCode',
        value: countryCode,
        apiFilter: {
          countryCode,
        },
        label: `Country Code: '${countryCode}'`,
      });
    }
    return filters;
  }

  componentDidMount() {
    const appliedFilters = this.computeFilters();
    this.props.onChange(appliedFilters);
    this.setState({ appliedFilters });
    this.fetchCountryCodes();
  }

  handleApply = () => {
    const appliedFilters = this.computeFilters();
    this.props.onChange(appliedFilters);
    this.setState({ appliedFilters });
    this.close();
  };

  handleReset = () => {
    this.setState({ ...getDefaultState(), ...getDefaultFilters() }, () => {
      const appliedFilters = this.computeFilters();
      this.props.onChange(appliedFilters);
      this.setState({ appliedFilters });
      this.close();
    });
  };

  handleRemoveFilter = (filter) => {
    const { appliedFilters } = this.state;
    const newFilters = appliedFilters.filter((c) => c.field !== filter.field);

    if (filter.field === 'dateRange') {
      const { useAllTime, from, to } = getDefaultFilters();
      this.setState({ useAllTime, from, to });
    } else {
      this.setState({ [filter.field]: !filter.value });
    }

    this.setState({ appliedFilters: newFilters }, () =>
      this.props.onChange(this.state.appliedFilters)
    );
    this.close();
  };

  fetchCountryCodes() {
    const ccMap = countries.getCountryCodeMap();
    const countryCodes = countries.getCountryCodes().map((cc) => ({
      key: cc,
      value: ccMap[cc],
      text: `${cc} (${ccMap[cc]})`,
    }));

    this.setState({ countryCodes });
  }

  render() {
    const {
      from,
      to,
      useAllTime,
      hideUsed,
      open,
      enableAutoPick,
      countryCodes,
      countryCode,
    } = this.state;

    return (
      <div style={{ display: 'flex' }}>
        <Modal
          size="tiny"
          open={open}
          onOpen={this.open}
          onClose={this.close}
          trigger={
            <Button basic primary={true} floated="left">
              <Icon name="filter" /> Filters
            </Button>
          }
          closeIcon>
          <Modal.Header>Filters</Modal.Header>
          <Modal.Content>
            <Form>
              <Grid divided>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Form.Field>
                      <Checkbox
                        onChange={(e, { checked }) => {
                          this.setState({
                            useAllTime: checked,
                          });
                        }}
                        toggle
                        checked={useAllTime}
                        label="All Time"
                      />
                    </Form.Field>
                    <div
                      onClick={() =>
                        this.setState({
                          useAllTime: false,
                        })
                      }>
                      <Form.Field disabled={useAllTime}>
                        <label>From</label>
                        <DatePicker
                          dateFormat="dd/MM/yyyy"
                          selected={from}
                          onChange={(date) => this.setState({ from: date })}
                          selectsStart
                          startDate={from}
                          endDate={to}
                        />
                      </Form.Field>
                      <Form.Field disabled={useAllTime}>
                        <label>To</label>
                        <DatePicker
                          dateFormat="dd/MM/yyyy"
                          selected={to || new Date()}
                          onChange={(date) => this.setState({ to: date })}
                          selectsEnd
                          startDate={from}
                          endDate={to}
                          minDate={from}
                        />
                      </Form.Field>
                    </div>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Form.Checkbox
                      label="Only show unused"
                      checked={hideUsed}
                      onChange={(e, { checked }) => {
                        this.setState({
                          hideUsed: checked,
                        });
                      }}
                      style={{ marginRight: '10px' }}
                      toggle
                    />
                    <Form.Checkbox
                      label="Auto Pick enabled"
                      checked={enableAutoPick}
                      onChange={(e, { checked }) => {
                        this.setState({
                          enableAutoPick: checked,
                        });
                      }}
                      style={{ marginRight: '10px' }}
                      toggle
                    />
                    <Form.Select
                      placeholder="Country Code"
                      options={countryCodes}
                      onChange={(e, { value }) =>
                        this.setState({ countryCode: value })
                      }
                      value={countryCode || ''}
                      search
                      clearable
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button basic content="Reset" onClick={this.handleReset} />
            <Button
              primary
              icon="check"
              content="Apply"
              onClick={this.handleApply}
            />
          </Modal.Actions>
        </Modal>

        <div style={{ marginLeft: '10px' }}>
          {this.state.appliedFilters.map((filter) => (
            <Label
              key={filter.field}
              size="large"
              style={{ marginLeft: '10px', lineHeight: '20px' }}>
              {filter.label}
              <Icon
                name="delete"
                onClick={() => this.handleRemoveFilter(filter)}
              />
            </Label>
          ))}
        </div>
      </div>
    );
  }
}
